import { addMinutes, format } from 'date-fns';
import React from 'react';
import { useParams } from 'react-router-dom';

import FilesFieldComponent from '@components/common/FilesFieldComponent/FilesFieldComponent';

import useTranslation from '@hooks/useTranslation';

import type { IAIReport } from '@model/aiReport';
import type { ICurrency } from '@model/currency';

import { getReportEntityName } from '@pages/ActionItem/helpers/getReportEntityName';

import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import MaintenanceDetails from '../MaintenanceDetails/MaintenanceDetails';
import MetricsFields from '../MetricsFields/MetricsFields';
import ReplacePartsComponent from '../ReplacedPartsComponent/ReplacedPartsComponent';
import TextFieldComponent from '../TextFieldComponent/TextFieldComponent';
import TextareaFieldComponent from '../TextareaFieldComponent/TextareaFieldComponent';
import style from './style.module.scss';

interface IProps {
  actionItemType?: string;
  report: IAIReport;
  isNoActions?: boolean;
  userTimezone: number;
  currency: ICurrency;
  costSavingTemplateCurrency: ICurrency | null;
}

const ReportDetails: React.FC<IProps> = ({
  actionItemType,
  isNoActions,
  report,
  userTimezone,
  currency,
  costSavingTemplateCurrency,
}) => {
  const locationSelectValue = selectAppLocationSelectValue();
  const { customerId } = useParams();
  const { t } = useTranslation();

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { data: profileData } = useUserProfileQuery();
  const currentDateFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const maintenancePerformedType = report?.text ? 'text' : 'no_actions_description';
  const reportEntityName = getReportEntityName(actionItemType, isNoActions, report.state);

  return (
    <div className={style.reportDetails}>
      {report.decline_reason && report.state != 1 && (
        <TextareaFieldComponent
          text={report.decline_reason}
          label={t('Decline reason')}
          hasTranslateField={true}
          translationKeys={{
            fields: 'decline_reason',
            entityName: reportEntityName,
            entityId: report.id,
          }}
          report={report}
        />
      )}
      {report.ww_comment && (
        <TextareaFieldComponent
          text={report.ww_comment}
          label={t('Waites Comment')}
          extraLabel={
            report?.time_submited &&
            report?.submited_by &&
            `${t('Verified by')} ${report.submited_by} (${format(
              addMinutes(report?.time_submited, userTimezone),
              currentDateFormat,
            )})`
          }
          hasTranslateField={true}
          translationKeys={{
            fields: 'ww_comment',
            entityName: reportEntityName,
            entityId: report.id,
          }}
          report={report}
        />
      )}
      {!!+report?.is_sensor_attached && (
        <div className={style.reportNotificationWrpr}>
          <span className={style.reportNotification}>
            {t('The sensor is securely attached to the equipment.')}
          </span>
        </div>
      )}
      {(actionItemType !== 'regular' || report.no_actions_type) && (
        <div className={style.extraInfo}>
          {+report.no_actions_type === 1 || report.no_actions_description
            ? t('No action is required.')
            : t('Maintenance was conducted on Waites equipment.')}
        </div>
      )}
      {actionItemType !== 'regular' && (
        <>
          <TextareaFieldComponent
            text={report?.[maintenancePerformedType]}
            label={t('Maintenance performed')}
            hasTranslateField={true}
            translationKeys={{
              fields: maintenancePerformedType,
              entityName: reportEntityName,
              entityId: report.id,
            }}
            report={report}
          />
          {!!report?.reportActions?.length && (
            <div>
              <div className={style.fieldLabel}>{t('Report actions')}</div>
              <div className={style.reportActions}>
                <ol>{report?.reportActions?.map(({ name }) => <li>{name}</li>)}</ol>
              </div>
            </div>
          )}
          {!!report?.ww_other_action && (
            <TextareaFieldComponent text={report.ww_other_action} label={t('Other actions')} />
          )}
          {!!report?.is_physical_damage && (
            <TextFieldComponent label={t('Physical damage')}>
              {t(report?.is_physical_damage ? 'Yes' : 'No')}
            </TextFieldComponent>
          )}
        </>
      )}
      {actionItemType === 'regular' && (
        <TextareaFieldComponent
          text={report?.[maintenancePerformedType]}
          label={t('Maintenance performed')}
          hasTranslateField={true}
          translationKeys={{
            fields: maintenancePerformedType,
            entityName: reportEntityName,
            entityId: report.id,
          }}
          report={report}
        />
      )}
      <MaintenanceDetails actionItemType={actionItemType} report={report} />
      <FilesFieldComponent files={report.file} />
      {!!report.cause && (
        <TextareaFieldComponent
          text={report.cause}
          label={t('Description')}
          hasTranslateField={true}
          translationKeys={{ fields: 'cause', entityName: reportEntityName, entityId: report.id }}
          report={report}
        />
      )}
      {+report?.no_actions_group_id === 6 && (
        <TextFieldComponent label={t('Cause category')}>{t(report.cause_category || '-')}</TextFieldComponent>
      )}
      <MetricsFields
        actionItemType={actionItemType}
        report={report}
        showTotalCostSavings={customerInfoData?.showTotalCostSavings}
        currency={currency}
        costSavingTemplateCurrency={costSavingTemplateCurrency}
      />
      <ReplacePartsComponent replacedParts={report?.replaced_parts} />
    </div>
  );
};

export default ReportDetails;
